import { BUSINESS_TIMEZONE } from '@nadoutong/contracts';
import type {
  OpsRiskDimension,
  OpsRiskGroup,
  OpsRiskItem,
  OpsRiskSeverity,
  OpsRiskStateStatus,
} from '@nadoutong/contracts';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOpsRisks, useSetOpsRiskState } from '../api/risks.ts';
import { useOpsUserLabel } from '../api/directory.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { isStale } from '../components/freshness.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  buttonClass,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  KpiCard,
  PageHeader,
  Panel,
  Select,
  Skeleton,
  Textarea,
} from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Risk workbench (epic #997 P1.3, #1005) — the replacement for the old
 * `/business/risk` card wall.
 *
 * Differences from the retired `BusinessRiskPage` projection (removed in
 * #1007):
 *   - reads the ADDITIVE `groups` view from `/v1/ops/risks`, so N
 *     same-type items render as ONE expandable row (`count = N`) instead
 *     of N identical cards;
 *   - severity + dimension + status filters;
 *   - ack / resolve / reopen per item via `POST /v1/ops/risks/state`
 *     (actor is session-derived server-side; the state persists in the
 *     platform-owned `ops_risk_states` table and reappears after a
 *     reload);
 *   - risk → traffic links use the exact `href` the aggregation
 *     computed, which already carries the measured `window` +
 *     `trafficClass`, so following it shows the same numbers.
 *
 * Dispatch Deck only: every visual comes from `src/ui/`; zero hex.
 */

const ALL = 'all';

const SEVERITY_ORDER: Record<OpsRiskSeverity, number> = {
  critical: 3,
  warning: 2,
  info: 1,
};

const SEVERITY_LABEL_KEYS: Record<OpsRiskSeverity, I18nKey> = {
  critical: 'risk_workbench.severity.critical',
  warning: 'risk_workbench.severity.warning',
  info: 'risk_workbench.severity.info',
};

const DIMENSION_LABEL_KEYS: Record<OpsRiskDimension, I18nKey> = {
  supply: 'risk.dimension.supply',
  finance: 'risk.dimension.finance',
  traffic: 'risk.dimension.traffic',
};

const STATUS_LABEL_KEYS: Record<OpsRiskStateStatus, I18nKey> = {
  open: 'risk_workbench.status.open',
  acked: 'risk_workbench.status.acked',
  resolved: 'risk_workbench.status.resolved',
};

const SEVERITY_FILTERS: readonly OpsRiskSeverity[] = ['critical', 'warning', 'info'];
const DIMENSION_FILTERS: readonly OpsRiskDimension[] = ['supply', 'finance', 'traffic'];
const STATUS_FILTERS: readonly OpsRiskStateStatus[] = ['open', 'acked', 'resolved'];

function severityChipTone(severity: OpsRiskSeverity): ChipTone {
  if (severity === 'critical') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'neutral';
}

function statusChipTone(status: OpsRiskStateStatus): ChipTone {
  if (status === 'resolved') return 'ok';
  if (status === 'acked') return 'signal';
  return 'neutral';
}

/** Effective status of an item — absent `state` means open. */
function itemStatus(item: OpsRiskItem): OpsRiskStateStatus {
  return item.state?.status ?? 'open';
}

interface StateTarget {
  readonly item: OpsRiskItem;
  readonly next: OpsRiskStateStatus;
}

export function RiskWorkbenchPage(): JSX.Element {
  const t = useT();
  // The workbench is the explicit read: it recomputes the balance-source
  // reconciliation (#2685), which also refreshes what the shell chip shows.
  const query = useOpsRisks('live');

  const [severity, setSeverity] = useState<OpsRiskSeverity | typeof ALL>(ALL);
  const [dimension, setDimension] = useState<OpsRiskDimension | typeof ALL>(ALL);
  const [status, setStatus] = useState<OpsRiskStateStatus | typeof ALL>(ALL);

  const data = query.data;

  // Derive the group view from the payload. The server sends `groups`
  // additively; if an older server omits it we fold locally so the page
  // never regresses to N cards.
  const groups = useMemo<readonly OpsRiskGroup[]>(() => {
    if (data === undefined) return [];
    if (data.groups !== undefined) return data.groups;
    return foldGroups(data.risks);
  }, [data]);

  const filteredGroups = useMemo<readonly OpsRiskGroup[]>(() => {
    const out: OpsRiskGroup[] = [];
    for (const group of groups) {
      if (dimension !== ALL && group.dimension !== dimension) continue;
      const items = group.items.filter((item) => {
        if (severity !== ALL && item.severity !== severity) return false;
        if (status !== ALL && itemStatus(item) !== status) return false;
        return true;
      });
      if (items.length === 0) continue;
      out.push({ ...group, items, count: items.length, severity: maxSeverity(items) });
    }
    return out;
  }, [groups, severity, dimension, status]);

  const counts = useMemo(() => summarize(data?.risks ?? []), [data]);

  return (
    <section className="flex flex-col gap-4" data-testid="risk-workbench-page">
      <PageHeader
        title={t('risk_workbench.title')}
        sub={t('risk_workbench.subtitle')}
        actions={
          data !== undefined ? (
            // The risk queue is a backlog, not a single windowed/comparative
            // aggregate (supply = trailing 24h, finance = open backlog,
            // traffic = the overview's own window), so it has no from/to or
            // comparison-period envelope. #1008 still surfaces data freshness:
            // the generatedAt timestamp anchored to the UTC+8 business
            // timezone, coloured on staleness.
            <span
              className={`font-mono text-micro ${isStale(data.generatedAt) ? 'text-warn' : 'text-paper-low'}`}
              data-testid="risk-workbench-updated"
            >
              {t('risk_workbench.updated')} {formatLocalTimestamp(data.generatedAt, { seconds: true })} ·{' '}
              {BUSINESS_TIMEZONE}
              {isStale(data.generatedAt) ? ` · ${t('cockpit.envelope.stale')}` : ''}
            </span>
          ) : undefined
        }
      />

      {query.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="risk-workbench-loading">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : query.isError || data === undefined ? (
        <EmptyState
          title={t('risk_workbench.error')}
          hint={query.error !== null ? coerceErrorMessage(query.error) : undefined}
          testId="risk-workbench-error"
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <KpiCard
              label={t('risk_workbench.summary.open')}
              value={counts.open}
              tone={counts.open > 0 ? 'warn' : 'default'}
              testId="risk-workbench-kpi-open"
            />
            <KpiCard
              label={t('risk_workbench.summary.critical')}
              value={counts.critical}
              tone={counts.critical > 0 ? 'bad' : 'default'}
              testId="risk-workbench-kpi-critical"
            />
            <KpiCard
              label={t('risk_workbench.summary.warning')}
              value={counts.warning}
              tone={counts.warning > 0 ? 'warn' : 'default'}
              testId="risk-workbench-kpi-warning"
            />
            <KpiCard
              label={t('risk_workbench.summary.acked')}
              value={counts.acked}
              testId="risk-workbench-kpi-acked"
            />
            <KpiCard
              label={t('risk_workbench.summary.resolved')}
              value={counts.resolved}
              tone={counts.resolved > 0 ? 'ok' : 'default'}
              testId="risk-workbench-kpi-resolved"
            />
          </div>

          <div
            className="flex flex-wrap items-end gap-3"
            role="group"
            aria-label={t('risk_workbench.title')}
          >
            <FormField label={t('risk_workbench.filter.severity')} fieldId="risk-workbench-filter-severity">
              <Select
                value={severity}
                onValueChange={(value) => setSeverity(value === ALL ? ALL : (value as OpsRiskSeverity))}
                options={[
                  { value: ALL, label: t('risk_workbench.filter.all') },
                  ...SEVERITY_FILTERS.map((value) => ({ value, label: t(SEVERITY_LABEL_KEYS[value]) })),
                ]}
                testId="risk-workbench-filter-severity"
              />
            </FormField>
            <FormField label={t('risk_workbench.filter.dimension')} fieldId="risk-workbench-filter-dimension">
              <Select
                value={dimension}
                onValueChange={(value) => setDimension(value === ALL ? ALL : (value as OpsRiskDimension))}
                options={[
                  { value: ALL, label: t('risk_workbench.filter.all') },
                  ...DIMENSION_FILTERS.map((value) => ({ value, label: t(DIMENSION_LABEL_KEYS[value]) })),
                ]}
                testId="risk-workbench-filter-dimension"
              />
            </FormField>
            <FormField label={t('risk_workbench.filter.status')} fieldId="risk-workbench-filter-status">
              <Select
                value={status}
                onValueChange={(value) => setStatus(value === ALL ? ALL : (value as OpsRiskStateStatus))}
                options={[
                  { value: ALL, label: t('risk_workbench.filter.all') },
                  ...STATUS_FILTERS.map((value) => ({ value, label: t(STATUS_LABEL_KEYS[value]) })),
                ]}
                testId="risk-workbench-filter-status"
              />
            </FormField>
          </div>

          <Panel caption={t('risk_workbench.title')} count={filteredGroups.length} padded>
            {filteredGroups.length === 0 ? (
              <EmptyState
                title={
                  counts.open === 0
                    ? t('risk_workbench.empty')
                    : t('risk_workbench.empty_filtered')
                }
                testId="risk-workbench-empty"
              />
            ) : (
              <div className="flex flex-col gap-2">
                {filteredGroups.map((group) => (
                  <RiskGroupRow key={group.groupKey} group={group} />
                ))}
              </div>
            )}
          </Panel>
        </>
      )}
    </section>
  );
}

function RiskGroupRow({ group }: { readonly group: OpsRiskGroup }): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const Caret = expanded ? ChevronDown : ChevronRight;

  return (
    <div
      className="overflow-hidden rounded-lg border border-line-soft bg-well"
      data-testid={`risk-workbench-group-${group.groupKey}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-label={expanded ? t('risk_workbench.group.collapse') : t('risk_workbench.group.expand')}
        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors duration-120 hover:bg-active"
      >
        <Caret aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
        <Chip tone={severityChipTone(group.severity)}>{t(SEVERITY_LABEL_KEYS[group.severity])}</Chip>
        <span className="min-w-0 truncate text-body font-medium text-paper">{group.title}</span>
        <span className="text-label text-paper-low">{t(DIMENSION_LABEL_KEYS[group.dimension])}</span>
        <span
          className="ml-auto rounded-full bg-signal-dim px-2 py-0.5 font-mono text-micro text-signal"
          data-testid={`risk-workbench-group-count-${group.groupKey}`}
        >
          {t('risk_workbench.group.count', { count: group.count })}
        </span>
      </button>
      {expanded ? (
        <div className="flex flex-col gap-2 border-t border-line-soft p-3">
          {group.items.map((item) => (
            <RiskItemRow key={item.id} item={item} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RiskItemRow({ item }: { readonly item: OpsRiskItem }): JSX.Element {
  const t = useT();
  const opsUserLabel = useOpsUserLabel();
  const [target, setTarget] = useState<StateTarget | null>(null);
  const status = itemStatus(item);

  return (
    <div
      className="rounded-lg bg-card px-4 py-3"
      data-testid={`risk-workbench-item-${item.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={severityChipTone(item.severity)}>{t(SEVERITY_LABEL_KEYS[item.severity])}</Chip>
            {status !== 'open' ? (
              <Chip tone={statusChipTone(status)} testId={`risk-workbench-item-state-${item.id}`}>
                {t('risk_workbench.item.state_by', {
                  status: t(STATUS_LABEL_KEYS[status]),
                  actor: opsUserLabel(item.state?.actor) || (item.state?.actor ?? ''),
                })}
              </Chip>
            ) : null}
          </div>
          <p className="m-0 mt-2 break-words text-dense text-paper-low">{item.detail}</p>
          {item.state !== undefined && item.state.note.length > 0 ? (
            <p className="m-0 mt-1 break-words text-micro text-paper-faint">{item.state.note}</p>
          ) : null}
          {item.occurredAt !== null ? (
            <span className="mt-1 block font-mono text-micro text-paper-low">
              {formatLocalTimestamp(item.occurredAt)}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          {status === 'open' ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setTarget({ item, next: 'acked' })}
                data-testid={`risk-workbench-ack-${item.id}`}
              >
                {t('risk_workbench.action.ack')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setTarget({ item, next: 'resolved' })}
                data-testid={`risk-workbench-resolve-${item.id}`}
              >
                {t('risk_workbench.action.resolve')}
              </Button>
            </>
          ) : (
            <>
              {status === 'acked' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setTarget({ item, next: 'resolved' })}
                  data-testid={`risk-workbench-resolve-${item.id}`}
                >
                  {t('risk_workbench.action.resolve')}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTarget({ item, next: 'open' })}
                data-testid={`risk-workbench-reopen-${item.id}`}
              >
                {t('risk_workbench.action.reopen')}
              </Button>
            </>
          )}
          <Link
            to={item.href}
            className={buttonClass('ghost', 'sm')}
            data-testid={`risk-workbench-target-${item.id}`}
          >
            <span>{t('risk_workbench.action.open_target')}</span>
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
      <RiskStateDialog target={target} onClose={() => setTarget(null)} />
    </div>
  );
}

const DIALOG_TITLE_KEYS: Record<OpsRiskStateStatus, I18nKey> = {
  acked: 'risk_workbench.dialog.ack_title',
  resolved: 'risk_workbench.dialog.resolve_title',
  open: 'risk_workbench.dialog.reopen_title',
};

const TOAST_KEYS: Record<OpsRiskStateStatus, I18nKey> = {
  acked: 'risk_workbench.toast.acked',
  resolved: 'risk_workbench.toast.resolved',
  open: 'risk_workbench.toast.reopened',
};

function RiskStateDialog({
  target,
  onClose,
}: {
  readonly target: StateTarget | null;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const mutation = useSetOpsRiskState();
  const [note, setNote] = useState('');

  function submit(): void {
    if (target === null || target.item.fingerprint === undefined) return;
    mutation.mutate(
      {
        fingerprint: target.item.fingerprint,
        status: target.next,
        note: note.trim().length > 0 ? note.trim() : undefined,
      },
      {
        onSuccess: () => {
          toast.success(t(TOAST_KEYS[target.next]));
          setNote('');
          onClose();
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) {
          setNote('');
          onClose();
        }
      }}
      title={target !== null ? t(DIALOG_TITLE_KEYS[target.next]) : ''}
      description={target?.item.title}
      closeLabel={t('common.close')}
      testId="risk-workbench-state-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={submit}
            data-testid="risk-workbench-state-submit"
          >
            {mutation.isPending ? t('risk_workbench.dialog.submitting') : t('risk_workbench.dialog.submit')}
          </Button>
        </>
      }
    >
      {target === null ? null : (
        <div className="flex flex-col gap-3">
          <p className="m-0 break-words text-dense text-paper-low">{target.item.detail}</p>
          <FormField label={t('risk_workbench.dialog.note_label')} fieldId="risk-workbench-state-note">
            <Textarea
              id="risk-workbench-state-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('risk_workbench.dialog.note_placeholder')}
              rows={3}
              data-testid="risk-workbench-state-note"
            />
          </FormField>
        </div>
      )}
    </Dialog>
  );
}

function maxSeverity(items: readonly OpsRiskItem[]): OpsRiskSeverity {
  let severity: OpsRiskSeverity = 'info';
  for (const item of items) {
    if (SEVERITY_ORDER[item.severity] > SEVERITY_ORDER[severity]) {
      severity = item.severity;
    }
  }
  return severity;
}

/** Local fold when the server omits `groups` (older payloads). */
function foldGroups(risks: readonly OpsRiskItem[]): OpsRiskGroup[] {
  const byType = new Map<string, OpsRiskItem[]>();
  for (const risk of risks) {
    if (risk.type === undefined) continue;
    const bucket = byType.get(risk.type) ?? [];
    bucket.push(risk);
    byType.set(risk.type, bucket);
  }
  const groups: OpsRiskGroup[] = [];
  for (const [, items] of byType.entries()) {
    const first = items[0];
    if (first === undefined || first.type === undefined) continue;
    groups.push({
      groupKey: first.type,
      dimension: first.dimension,
      severity: maxSeverity(items),
      title: first.title,
      count: items.length,
      items,
    });
  }
  return groups;
}

function summarize(risks: readonly OpsRiskItem[]): {
  open: number;
  critical: number;
  warning: number;
  acked: number;
  resolved: number;
} {
  let open = 0;
  let critical = 0;
  let warning = 0;
  let acked = 0;
  let resolved = 0;
  for (const risk of risks) {
    const status = itemStatus(risk);
    if (status === 'acked') acked += 1;
    else if (status === 'resolved') resolved += 1;
    else open += 1;
    if (risk.severity === 'critical') critical += 1;
    else if (risk.severity === 'warning') warning += 1;
  }
  return { open, critical, warning, acked, resolved };
}
